import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { BACKEND_URL } from './backendConfig';
import { richToHtml } from './RichText';
import { taskStatusLabel } from './ticketStatus';
import AssignSamplePeople from './AssignSamplePeople';
import TicketMessages from './TicketMessages';

const PUBLIC_BASE = `${BACKEND_URL}/api/development/public`;

const PRIORITY_COLOR = { High: '#DC2626', Medium: '#D97706', Low: '#16A34A' };

/* The receiving team's answers are VALUES — the word posted back is the English one the server and
   the report key off — so only the text drawn in the box goes through the dictionary. A word the
   server adds later and this map does not know is drawn as it is. */
const ASSIGNEE_LABEL_KEYS = {
  'Not started': 'xTicket.asNotStarted',
  'In Progress': 'xTicket.asInProgress',
  Done: 'xTicket.asDone',
  Cancelled: 'xTicket.asCancelled',
};

// Public, unauthenticated ticket status-update page (ports WebApp.gs _serveTaskPage).
// The token in the URL is the only credential — no login required.
function TaskStatus() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /* 'Not started' is where a ticket nobody has answered sits, so it is what this page opens on —
     Max, 2026-09-16 and 2026-09-17: the receiving team answers Not started · In Progress · Done ·
     Cancelled. The list only ever arrives from the server; these are the fallback for a fetch that
     failed, which is why they are written out once and nowhere else. */
  const [status, setStatus] = useState('Not started');
  /* The three come from the server, which reads them out of the status dimension. Hard-coding them
     here is how the page and the database would drift the first time a fourth is added. */
  const [statusOptions, setStatusOptions] = useState(['Not started', 'In Progress', 'Done', 'Cancelled']);
  const [note, setNote] = useState('');
  /* WHO is answering — Jay, 2026-09-16: "I want to know exactly who answered. (PIC
     Name)". This page is deliberately unauthenticated (the receiving team has no
     portal account), so the only way the portal can know is to ask. */
  const [pic, setPic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${PUBLIC_BASE}/task/${encodeURIComponent(token)}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || t('xTicket.invalidLink'));
        setTask(data.task);
        // Reopen on THEIR last reply, not on the requester's status — the two are separate now.
        if (Array.isArray(data.statusOptions) && data.statusOptions.length) setStatusOptions(data.statusOptions);
        /* A reply from before 2026-09-17 can say "Pending" or "Cancelled", words this page no longer
           offers. Keeping the value would put a dead option in the box and silently write it back on
           the next submit, so the page falls to Not started and the person picks again — what they
           meant by "Pending" is exactly what the two new answers were added to tell apart. */
        const prior = data.task.assigneeStatus || '';
        const offered = Array.isArray(data.statusOptions) && data.statusOptions.length
          ? data.statusOptions : ['Not started', 'In Progress', 'Done', 'Cancelled'];
        setStatus(offered.includes(prior) ? prior : 'Not started');
        setNote(data.task.assigneeNote || '');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* The two conditions the Submit button waits on, named once so the button, its tooltip and the
     line under it cannot disagree about which is missing. */
  const cancelling = status === 'Cancelled';
  const ready = !!pic.trim() && (!cancelling || !!note.trim());

  const submit = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${PUBLIC_BASE}/task/${encodeURIComponent(token)}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, note, pic }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || t('xTicket.updateFailed'));
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isClosed = task && (task.status === 'Done' || task.status === 'Cancelled');
  const pColor = task ? (PRIORITY_COLOR[task.priority] || '#6B7280') : '#6B7280';

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', background: '#F3F4F6', margin: 0, minHeight: '100vh', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', maxWidth: 620, margin: '40px auto', boxShadow: '0 4px 16px rgba(0,0,0,.1)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1F3864', marginBottom: 20 }}>{t('xTicket.pageTitle')}</div>
        {loading ? (
          <div style={{ color: '#6B7280' }}>{t('xTicket.loadingTicket')}</div>
        ) : error ? (
          <div style={{ color: '#DC2626', fontWeight: 600 }}>{error}</div>
        ) : !task ? (
          <div style={{ color: '#DC2626', fontWeight: 600 }}>{t('xTicket.invalidLink')}</div>
        ) : (
          <>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, color: '#111' }}>{task.name}</h2>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
              {t('xTicket.projectAssigned', { project: task.projectId, assignee: task.assignee || '—' })}
            </div>
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#fff', marginRight: 6, background: pColor }}>{task.priority}</span>
            {task.taskType && <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#fff', marginRight: 6, background: '#6B7280' }}>{task.taskType}</span>}
            {task.dueDate && <span style={{ fontSize: 12, color: '#6B7280' }}>{t('xTicket.dueOn', { date: task.dueDate })}</span>}
            {task.description && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 5px' }}>{t('xTicket.description')}</label>
                <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, fontSize: 13, color: '#374151', marginBottom: 12, whiteSpace: 'pre-wrap' }}>{task.description}</div>
              </>
            )}
            {task.emailBody && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 5px' }}>{t('xTicket.emailContent')}</label>
                <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 14, fontSize: 12, lineHeight: 1.45, color: '#1F2937', whiteSpace: 'pre-wrap', maxHeight: 280, overflow: 'auto' }}>{task.emailBody}</div>
              </>
            )}
            {task.attachmentUrl && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 5px' }}>{t('xTicket.attachment')}</label>
                <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, fontSize: 13, marginBottom: 12 }}>
                  <a href={task.attachmentUrl} target="_blank" rel="noopener noreferrer">{t('xTicket.openAttachment')}</a>
                </div>
              </>
            )}
            {task.note && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 5px' }}>{t('xTicket.noteFromRequester')}</label>
                {/* The note carries the requester's own formatting since 2026-09-07. It is safe to
                    render because taskNoteSanitize filters it on the way into the database, not on
                    the way out — a legacy plain-text note comes back escaped with its line breaks. */}
                <div className="rt-view" style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, fontSize: 13, color: '#374151', marginBottom: 12 }}
                  dangerouslySetInnerHTML={{ __html: richToHtml(task.note) }} />
              </>
            )}
            {/* ASSIGNING PEOPLE SITS ON THE SAME PAGE AS THE REPLY — Jay, 2026-10-01: "the link inside
                the ticket should open a page where he can assign people". There is ONE link in the
                ticket, and the receiving team still has to answer the ticket, so this is added to
                the page rather than replacing it. It is above the reply box because assigning is
                what the person came to do; the status is what they leave on the way out.
                Drawn for every ticket: the component says so itself when the project has no sample
                yet, which is more use than a section that silently is not there. */}
            <div style={{ margin: '18px 0', padding: 14, border: '1px solid #E2E8F0', borderRadius: 10, background: '#F8FAFC' }}>
              <AssignSamplePeople
                api={`/api/development/public/assign/${encodeURIComponent(token)}`}
                by={pic}
              />
            </div>
            {done ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#16A34A', fontSize: 16, fontWeight: 600 }}>{t('xTicket.updatedOk')}</div>
            ) : isClosed ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#16A34A', fontSize: 16, fontWeight: 600 }}>
                {/* The MANAGER's word, so it is printed the way every other screen prints it —
                    this page is the one place the receiving team reads it, and "Done" here would
                    look like their own answer rather than the ticket being closed. */}
                <Trans
                  t={t}
                  i18nKey="xTicket.alreadyMarked"
                  values={{ status: taskStatusLabel(task.status) }}
                  components={{ b: <strong /> }}
                />
                <br />
                {t('xTicket.noFurtherAction')}
              </div>
            ) : (
              <>
                {/* The name comes FIRST and is required: asked after the status it reads as an
                    afterthought, and the whole point is that the requester learns who answered. */}
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 5px' }}>
                  {t('xTicket.yourName')} <span style={{ color: '#B91C1C' }}>*</span>
                </label>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                  {t('xTicket.yourNameHint')}
                </div>
                <input
                  value={pic}
                  onChange={(e) => setPic(e.target.value)}
                  placeholder={t('xTicket.yourNamePh')}
                  maxLength={120}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 5px' }}>{t('xTicket.reportStatus')}</label>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                  {t('xTicket.reportStatusHint')}
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}>
                  {statusOptions.map((o) => (
                    <option key={o} value={o}>{ASSIGNEE_LABEL_KEYS[o] ? t(ASSIGNEE_LABEL_KEYS[o]) : o}</option>
                  ))}
                </select>
                {/* ONE BOX, REQUIRED ONLY WHEN IT HAS TO BE — Amy Nguyen via Max, 2026-09-17: a
                    cancellation has to say why, because "cancelled" on its own could mean no more
                    sample, or dropped, or something else, and the team that raised it has to write
                    back and ask. A SECOND box called Reason would have left two free-text fields on
                    a form somebody fills in once; the note IS the reason, so it changes its name and
                    its rule with the answer above it. */}
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 5px' }}>
                  {cancelling ? t('xTicket.whyCancelled') : t('xTicket.noteOptional')}
                  {cancelling && <span style={{ color: '#B91C1C' }}> *</span>}
                </label>
                {cancelling && (
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                    {t('xTicket.cancelHint')}
                  </div>
                )}
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={cancelling ? t('xTicket.cancelPh') : t('xTicket.notePh')}
                  style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${cancelling && !note.trim() ? '#FCA5A5' : '#D1D5DB'}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', height: 90, resize: 'vertical', boxSizing: 'border-box' }}
                />
                {/* Refused here rather than accepted and reported as "(not given)": the mail is
                    the whole point of the page, and a mail that cannot say who answered is the
                    thing Jay asked us to fix. */}
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting || !ready}
                  title={ready ? '' : (pic.trim() ? t('xTicket.needReason') : t('xTicket.needName'))}
                  style={{ width: '100%', padding: 12, background: ready ? '#1F3864' : '#9CA3AF', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: ready ? 'pointer' : 'not-allowed', marginTop: 18 }}
                >
                  {submitting ? t('xTicket.submitting') : t('xTicket.submit')}
                </button>
                {/* One line, naming the one thing still missing — two warnings at once on a form
                    this short reads as a telling-off. */}
                {!ready && (
                  <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 8, textAlign: 'center' }}>
                    {!pic.trim()
                      ? t('xTicket.needNameBelow')
                      : t('xTicket.needReasonBelow')}
                  </div>
                )}
              </>
            )}
            {/* Questions and answers on this ticket (Young, 2026-10-27): the receiving team reads what was asked and answers
                here by name, with a file if needed — open on a closed ticket too. The name typed above fills it in. */}
            <TicketMessages token={token} pic={pic} />
          </>
        )}
      </div>
    </div>
  );
}

export default TaskStatus;
